import styled from "@emotion/styled";
import type { FC, ReactNode } from "react";
import { APP_BREAKPOINT } from "src/constants/layout";
import AvatarStack from "src/ui/components/CareTeamSection/AvatarStack";
import type { CareTeamCardVariant } from "src/ui/components/CareTeamSection/CardBackground";
import CardBackground from "src/ui/components/CareTeamSection/CardBackground";

/**
 * Contents of the top-left badge on a Care Team role card.
 *
 * All roles render the same way: 1–3 overlapping avatars followed by a
 * circular label pill. Regular roles use a "+N" count label; Sophie uses
 * "24/7" to communicate always-on availability instead of a member count.
 */
export interface CareTeamCardBadge {
  avatars: string[];
  label: string;
}

const Card = styled.button`
  position: relative;
  flex: 0 0 auto;
  width: 128px;
  aspect-ratio: 159 / 200;
  border: 0;
  border-radius: 6px;
  overflow: hidden;
  background: var(--color-cream, #fff);
  box-shadow:
    0 12px 28px rgba(69, 49, 22, 0.14),
    0 4px 10px rgba(69, 49, 22, 0.08);
  scroll-snap-align: start;
  cursor: pointer;
  padding: 0;
  text-align: left;
  font: inherit;
  color: inherit;
  transition:
    transform 0.15s ease,
    box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid var(--Accessible-Blue, #3973da);
    outline-offset: 2px;
  }

  @media (min-width: ${APP_BREAKPOINT}px) {
    width: 159px;
  }
`;

const BackgroundLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0.75rem;
  box-sizing: border-box;
`;

const Title = styled.h3`
  margin: 0;
  color: var(--color-charcoal);
  overflow: hidden;
`;

interface Props {
  /**
   * Card title node — typically a `<Translate>` for a `.card` key whose
   * value contains a `<br>` to force the last word onto its own line
   * (e.g. "Patient Care<br>Advocates").
   */
  title: ReactNode;
  variant: CareTeamCardVariant;
  badge: CareTeamCardBadge;
  onClick?: () => void;
  /**
   * Accessible label announced by screen readers. Should be the full,
   * unbroken role name so screen readers don't announce the visual line
   * break as a pause.
   */
  ariaLabel?: string;
}

const CareTeamRoleCard: FC<Props> = ({
  title,
  variant,
  badge,
  onClick,
  ariaLabel
}) => {
  return (
    <Card type="button" onClick={onClick} aria-label={ariaLabel}>
      <BackgroundLayer>
        <CardBackground variant={variant} />
      </BackgroundLayer>
      <Content>
        <div>
          <AvatarStack avatars={badge.avatars} label={badge.label} />
        </div>
        <Title className="as-body2">{title}</Title>
      </Content>
    </Card>
  );
};

export default CareTeamRoleCard;
